/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import HealthDataDisclosure from './HealthDataDisclosure';
import HealthDataSummary from './HealthDataSummary';

const STEPS = ['Prolific ID', 'Connect Google Health', 'Completion Code'];

// ─── Shared styles ────────────────────────────────────────────────────────────
const mono = { fontFamily: "'DM Mono', monospace" };
const blue = '#0A66C2';
const gray = { text: '#94A6B8', border: '#D6E2EB', bg: '#FBFCFE', dark: '#131B20', mid: '#4C5967' };

function Stepper({ activeStep }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
      {STEPS.map((label, i) => (
        <Fragment key={label}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 500, ...mono,
              background: i < activeStep ? blue : i === activeStep ? '#EFF6FF' : '#fff',
              color: i < activeStep ? '#fff' : i === activeStep ? blue : gray.text,
              border: `2px solid ${i <= activeStep ? blue : gray.border}`,
              transition: 'all 150ms',
            }}>
              {i < activeStep ? '✓' : i + 1}
            </div>
            <span style={{
              fontSize: 10, ...mono, whiteSpace: 'nowrap',
              color: i === activeStep ? gray.dark : gray.text,
              fontWeight: i === activeStep ? 500 : 400,
            }}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, margin: '0 6px', marginBottom: 20,
              background: i < activeStep ? blue : gray.border,
              transition: 'background 150ms',
            }} />
          )}
        </Fragment>
      ))}
    </div>
  );
}
Stepper.propTypes = { activeStep: PropTypes.number.isRequired };

function Label({ children }) {
  return (
    <p style={{ fontSize: 14, color: gray.mid, lineHeight: 1.65, marginBottom: 20 }}>
      {children}
    </p>
  );
}
Label.propTypes = { children: PropTypes.node };

function PrimaryButton({ onClick, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '9px 22px', borderRadius: 8, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: disabled ? gray.border : `linear-gradient(to bottom, #0A66C2, #0959AA)`,
        color: disabled ? gray.text : '#fff',
        fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500,
        boxShadow: disabled ? 'none' : 'inset 0 1px rgba(255,255,255,0.15), 0 0 0 1px #064079',
        transition: 'all 80ms',
      }}
    >
      {children}
    </button>
  );
}
PrimaryButton.propTypes = { onClick: PropTypes.func, disabled: PropTypes.bool, children: PropTypes.node };

function OutlineButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '9px 22px', borderRadius: 8, cursor: 'pointer',
        background: 'transparent', color: gray.mid,
        border: `1px solid ${gray.border}`,
        fontFamily: "'DM Sans', sans-serif", fontSize: 14,
        transition: 'all 80ms',
      }}
    >
      {children}
    </button>
  );
}
OutlineButton.propTypes = { onClick: PropTypes.func, children: PropTypes.node };

// ─── Main component ───────────────────────────────────────────────────────────
function ProlificRegistrationStepper({
  pid, activeStep, loading, participantId,
  onPidChange, onStorePid, onInitiateFitbitAuth, onRenewRegistration,
}) {
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: `3px solid ${gray.border}`,
          borderTopColor: blue,
          animation: 'ja-spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes ja-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 17, fontWeight: 500, color: gray.dark, margin: '0 0 20px', textAlign: 'center' }}>
        Registration Wizard
      </h2>

      <Stepper activeStep={activeStep} />

      {/* Step 0 — Prolific ID */}
      {activeStep === 0 && (
        <div>
          <Label>
            Please enter your <strong>Prolific ID</strong> below and press <strong>Next</strong>.
          </Label>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12, color: gray.mid, display: 'block', marginBottom: 5, fontWeight: 500 }}>
              Prolific ID
            </label>
            <input
              type="text"
              value={pid || ''}
              onChange={(e) => onPidChange(e.target.value)}
              placeholder="Enter your Prolific ID"
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 14,
                fontFamily: "'DM Mono', monospace",
                border: `1px solid ${pid && pid.length < 24 ? '#FCA5A5' : gray.border}`,
                background: '#fff', color: gray.dark, outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 100ms',
              }}
              onFocus={(e) => e.target.style.borderColor = blue}
              onBlur={(e) => e.target.style.borderColor = pid && pid.length < 24 ? '#FCA5A5' : gray.border}
            />
            {pid && pid.length < 24 && (
              <p style={{ fontSize: 12, color: '#C0392B', margin: '5px 0 0' }}>
                Prolific ID must be at least 24 characters
              </p>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <PrimaryButton onClick={onStorePid} disabled={!pid || pid.length < 24}>
              Next →
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* Step 1 / 2 — Google Health connection */}
      {(activeStep === 1 || activeStep === 2) && (
        <div>
          <Label>
            Press <strong>Connect Google Health</strong> below. You will be redirected to Google to sign in and authorize access to your Fitbit data.
          </Label>
          <HealthDataDisclosure />
          <div style={{
            background: '#EFF6FF', border: '1px solid rgba(10,102,194,0.2)',
            borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#1D4ED8',
          }}>
            Google may first show a <strong>Google hasn&apos;t verified this app</strong> notice while our review is in progress; press <strong>Continue</strong> (or <strong>Advanced</strong>, then <strong>Go to jamasp.app</strong>). When asked which data to share, tick <strong>Select all</strong>, then press <strong>Continue</strong>.
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <OutlineButton onClick={onRenewRegistration}>← Back</OutlineButton>
            <PrimaryButton onClick={onInitiateFitbitAuth}>Connect Google Health</PrimaryButton>
          </div>
        </div>
      )}

      {/* Step 3 — Completion code */}
      {activeStep >= 3 && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', background: '#ECFDF5',
            border: '2px solid #6EE7B7', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 16px', fontSize: 22,
          }}>
            ✓
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 500, color: gray.dark, margin: '0 0 8px' }}>
            You&apos;re all set!
          </h3>
          <p style={{ fontSize: 14, color: gray.mid, marginBottom: 20 }}>
            Your completion code is:
          </p>
          <div style={{
            background: gray.bg, border: `1px solid ${gray.border}`,
            borderRadius: 8, padding: '14px 20px', marginBottom: 20,
            ...mono, fontSize: 18, fontWeight: 500, color: blue,
            letterSpacing: '0.5px', wordBreak: 'break-all',
          }}>
            {participantId}
          </div>
          <p style={{ fontSize: 13, color: gray.mid, lineHeight: 1.65 }}>
            Copy and paste this code back in Qualtrics to complete the study and receive payment.
          </p>
          <HealthDataSummary />
        </div>
      )}
    </div>
  );
}

ProlificRegistrationStepper.propTypes = {
  pid: PropTypes.string,
  activeStep: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  participantId: PropTypes.string,
  onPidChange: PropTypes.func.isRequired,
  onStorePid: PropTypes.func.isRequired,
  onInitiateFitbitAuth: PropTypes.func.isRequired,
  onRenewRegistration: PropTypes.func.isRequired,
};

export default React.memo(ProlificRegistrationStepper);
